import { type ReactNode } from 'react';
import { Pencil, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RoleBadge } from '@/components/ui/role-badge';
import { cn } from '@/utils/cn';
import type { Approacher } from '../types';
import { STATUS_LABELS, STATUS_VARIANTS } from './account-status-meta';

const STRIKE_BASE = 'transition-colors';

function Dot({ active }: { active: boolean }): ReactNode {
  return (
    <X
      size={14}
      strokeWidth={3}
      className={cn(STRIKE_BASE, active ? 'text-error' : 'text-border-default')}
    />
  );
}

function StrikePills({ count }: { count: number }): ReactNode {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={`${count}/3 strikes esta semana`}
      aria-label={`${count} de 3 strikes`}
    >
      <Dot active={count > 0} />
      <Dot active={count > 1} />
      <Dot active={count > 2} />
    </span>
  );
}

function SanctionPills({ count }: { count: number }): ReactNode {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={`${count}/5 sanciones de por vida`}
      aria-label={`${count} de 5 sanciones`}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <Dot key={i} active={count > i} />
      ))}
    </span>
  );
}

function StatusEditButton({
  status,
  onEdit,
}: {
  status: Approacher['account_status'];
  onEdit: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="rounded-sm transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-red/30"
      title="Editar estado"
      aria-label="Editar estado"
    >
      <Badge variant={STATUS_VARIANTS[status]}>
        <span className="inline-flex items-center gap-1">
          {STATUS_LABELS[status]}
          <Pencil size={10} />
        </span>
      </Badge>
    </button>
  );
}

export function ProfilePills({
  a,
  onStatusEdit,
}: {
  a: Approacher;
  onStatusEdit: () => void;
}): ReactNode {
  const strikes = a.weekly_strikes ?? a.strikes ?? 0;
  const sanctions = a.sanctions_count ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-xs">
      <StatusEditButton status={a.account_status} onEdit={onStatusEdit} />
      <RoleBadge role={a.role} />
      <StrikePills count={strikes} />
      <SanctionPills count={sanctions} />
    </div>
  );
}
